<template>
    <div v-if="game">
        <b-card class="purchase mb-2" no-body>
            <b-card-header class="ml-1 pb-3 pr-0 d-none d-md-block">
                <div class="d-flex justify-content-between align-items-center">
                    <h4 class = "font-weight-semi-bold">{{ 'buy_a_ticket' | lang }}</h4>
                    <h6 class = "font-weight-semi-bold text-right">{{ 'balance' | lang }}: {{ balance | currency }}</h6>
                </div>
            </b-card-header>
            <b-card-body class="mode pt-0 pr-0">
                <h6 class="mb-2 font-weight-semi-bold">
                    <b-badge pill variant="light">1</b-badge>
                    <span v-if="game.name == 'Dice'">{{ 'variants' | lang }}</span>
                    <span v-else>{{ 'symbols' | lang }}</span>
                    <a v-if="explanation" id="explanation" href='#' @click.prevent.stop=""><fa-icon icon="question-circle"></fa-icon></a>
                </h6>
                <b-tooltip v-if="explanation" target="explanation" :title="explanation" custom-class="explanation-tooltip"></b-tooltip>
                <b-tabs pills nav-class="switches pb-2">
                    <b-tab :title="lang('purchase.choose_symbols')" active>
                        <purchase-symbol :game-id="purchaseGameId"
                            :count-id="countId"
                            :selected-items="gamesItems"
                            @add="addGameItem"
                            @remove="removeGameItem">
                        </purchase-symbol>
                    </b-tab>
                    <template v-if="this.game.name != 'Dice'">
                        <b-tab :title="lang('favorites.title')" :active.sync="favoritesActive">
                            <favorite-container :game-id="purchaseGameId"
                                :is-correct-count="isCorrectCount"
                                :is-active="favoritesActive"
                                :games-items="gamesItems"
                                :count-id="countId"
                                @choose="chooseFavorite">
                            </favorite-container>
                        </b-tab>
                        <b-tab :active.sync="suggestionsActive">
                            <template v-slot:title>
                                <div class="suggestions-title">
                                    <span>{{ 'suggestions.title' | lang }}</span>
                                    <b-badge class="d-none d-sm-inline new" variant="danger">{{ 'suggestions.new' | lang }}</b-badge>
                                </div>
                            </template>
                            <suggestions-container :game-id="purchaseGameId"
                                :count-id="countId"
                                @choose="chooseFavorite">
                            </suggestions-container>
                        </b-tab>
                        <b-button variant="link" class="right" @click="randomize" :disabled="suggestionsActive"><fa-icon icon="random"></fa-icon> {{ 'random' | lang }}</b-button>
                    </template>
                </b-tabs>
            </b-card-body>
            <b-card-body class="pt-0 pr-0 pl-1 pb-2 pr-md-3 pl-lg-3 pb-md-3">
                <random-container :game-id="purchaseGameId"
                    v-if="this.game.name != 'Dice' && this.game.name != 'Jokerjackpot'"
                    :count-id="countId"
                    @selectCount="setCountId">
                </random-container>
                <amount :game-id="purchaseGameId"
                    :selected-id="amountId"
                    :count="(this.game.name != 'Dice' && this.game.name != 'Jokerjackpot') ? 3 : 2"
                    @select="setAmountId">
                </amount>
                <combination :game-id="purchaseGameId"
                    :selected-id="combinationId"
                    :count="this.game.name != 'Dice' ? 4 : 3"
                    @select="setCombinationId">
                </combination>
                <continue-count :game-id="purchaseGameId"
                    :selected="continueCount"
                    :count="hasCombinations ? 5 : (this.game.name == 'Dice' || this.game.name == 'Jokerjackpot' ? 3 : 4)"
                    @select="setContinueCount">
                </continue-count>
            </b-card-body>
            <b-card-body class="d-flex ml-auto mr-auto align-self-center w-100 pr-0 pr-md-2">
                <div class="text-center w-100">
                    <pool-info :game-id="purchaseGameId"></pool-info>
                    <div class="pb-3 d-flex justify-content-between d-md-block">
                        <span class="d-block d-md-none">{{ 'balance' | lang }}: <strong>{{ balance | currency }}</strong></span> 
                        <span>{{ 'amount' | lang }}: <strong>{{ ticketAmount | currency }}</strong></span>
                    </div>
                    <span class="text-danger" v-if="!canPurchase && purchaseInfo == 'restricted'">{{ 'shop_puchase_restricted' | lang }}</span>
                    <draw-progress v-else-if="!canPurchase" :game-id="purchaseGameId"></draw-progress>
                    <b-button v-else variant="success" :disabled="!isCorrectCount || !canPurchase || isPurchasing" @click="$emit('purchase')" class="font-weight-semi-bold purchase-button">
                        <b-spinner small v-show="isPurchasing"></b-spinner> {{ 'buy_a_ticket' | lang }}
                    </b-button>
                    <alert v-if="alertPayload" :payload="alertPayload"> </alert>
                </div>
            </b-card-body>
        </b-card>
    </div>
</template>

<script>
import { values, last, head, sampleSize, shuffle } from 'lodash';
import lang from '@tv/filters/lang';
import { mapGetters, mapState, mapMutations } from "vuex";
import Axios from "axios";
import { syncFrameHeight } from "@tv/helpers";

import PurchaseSymbol from "@tv/components/partials/purchase/symbol/Shim";
import FavoriteContainer from "@tv/components/partials/purchase/FavoriteContainer";
import RandomContainer from "@tv/components/partials/purchase/RandomContainer";
import SuggestionsContainer from "@tv/components/partials/purchase/SuggestionsContainer";
import Amount from "@tv/components/partials/purchase/Amount";
import ContinueCount from "@tv/components/partials/purchase/ContinueCount";
import Combination from "@tv/components/partials/purchase/Combination";
import PoolInfo from "@tv/components/partials/purchase/PoolInfo";
import DrawProgress from "@tv/components/partials/purchase/DrawProgress";
import Alert from "@tv/components/partials/Alert";

export default {
    components: { PurchaseSymbol, Amount, ContinueCount, Combination, FavoriteContainer, SuggestionsContainer, RandomContainer, PoolInfo, DrawProgress, Alert },

    props: [ 'isPurchasing', 'usingSymbols', 'alertPayload' ],
    
    data() {
        return {
            gamesItems: [],
            amountId: undefined,
            countId: 1,
            continueCount: 1,
            combinationId: 1,
            action: undefined,

            favoritesActive: false,
            suggestionsActive: false,
        };
    },

    computed: {
        ...mapState([
            'purchaseGameId',
            'allowPurchase',
            'allowPurchaseInfo',
            'selection',
            'isLoading',
            'selectionIsAdded',
            'selectionsOfGames',
            'balance',
        ]),

        game() {
            return Config.games[this.purchaseGameId];
        },

        hasCombinations() {
            return this.game.name == 'Interjackpot';
        },


        isCorrectCount() {
            if (!this.purchaseGameId)
                return;
            
            const count = Config.games[this.purchaseGameId].counts[this.countId];
            const combination = Config.games[this.purchaseGameId].combinations[this.combinationId];

            if (!count)
                return;

            let requiredCount = count.count;

            if (combination) {
                requiredCount += combination.combinators;
            }

            if (requiredCount == this.gamesItems.length) {
                return true;
            }
        },

        canPurchase() {
            return this.allowPurchase[this.purchaseGameId];
        },

        purchaseInfo() {
            const info = this.allowPurchaseInfo[this.purchaseGameId];
            return info == '' ? false : info;
        },

        addCardBgVariant() {
            if (!this.canPurchase)
                return 'danger-light';
            
            if(!this.isCorrectCount)
                return 'warning-light';
            
            return 'success-light';
        },

        ticketAmount() {
            if(this.game.amounts[this.amountId])
            {
                switch(this.game.name) {
                    case '5_36':
                    case '7_42':
                        return this.game.amounts[this.amountId].amount * this.continueCount;
                    case 'Interjackpot':
                    case 'Jokerjackpot':
                        return this.game.amounts[this.amountId].amount * this.continueCount * this.game.combinations[this.combinationId].combinations;
                    case 'Dice':
                        return this.game.amounts[this.amountId].amount * this.continueCount; 
                }
            }
        },

        explanation() {
            switch(this.game.name) {
                case 'Jokerjackpot':
                    return '(*) ' + this.$options.filters.lang('jokerjackpot.combinations_explain');
                case 'Dice':
                    return this.$options.filters.lang('rules.dice.first') + ' ' + this.$options.filters.lang('rules.dice.second'); 
                default:
                    return false;
            }
        }
    },

    methods: {
        ...mapMutations(['setSelection', 'setSelectionAdded', 'setSelectionOfGame', 'updateFavorites']),
        
        lang,

        chooseFavorite(ids) {
            this.gamesItems = [ ...ids ];
            this.action = 'item';
            this.favoritesActive = false;
            this.suggestionsActive = false;
            this.updateSelections();
            this.$emit('choice', 'favorite');
        },

        addGameItem(gameItemId) {
            if (this.game.name == 'Dice') {
                this.gamesItems = [];
            } else {
                this.gamesItems = this.gamesItems.filter(itemId => itemId != gameItemId);
            }

            this.gamesItems.push(gameItemId);
            this.action = 'item';
            this.updateSelections();
            this.$emit('choice', 'manual');
        },

        removeGameItem(gameItemId) {
            this.gamesItems = this.gamesItems.filter(itemId => itemId != gameItemId);
            this.action = 'item';
            this.updateSelections();
            this.$emit('choice', 'manual');
        },

        setAmountId(amountId) {
            this.amountId = amountId;
            this.action = 'amount';
            this.updateSelections();
        },

        setCountId(countId) {
            this.countId = countId;
            this.action = 'count';
            this.updateSelections();
        },

        setContinueCount(continueCount) {
            this.continueCount = continueCount;
            this.action = 'continueCount';
            this.updateSelections();
        },

        setCombinationId(combinationId) {
            this.combinationId = combinationId;
            this.action = 'combination';
            this.updateSelections();
        },

        syncSelection() {
            this.setSelection({
                gamesItems: [ ...this.gamesItems ],
                amountId: this.amountId,
                countId: this.countId,
                continueCount: this.continueCount,
                combinationId: this.combinationId,
            });

            this.setSelectionOfGame({
                gameId: this.purchaseGameId,
                countId: this.countId,
                amountId: this.amountId,
            });
        },

        randomize() {
            const game = this.game;
            const count = game.counts[this.countId];

            this.gamesItems = [];

            if (game.name == 'Jokerjackpot') {
                const flasked = {};

                for (const [ id, item ] of shuffle(Object.entries(game.items))) {
                    if (item.symbol == '*' || flasked[item.flask])
                        continue;

                    flasked[item.flask] = id;
                }

                this.gamesItems = Object.values(flasked);
            } else {
                if (count) {
                    this.gamesItems = sampleSize(Object.keys(game.items), count.count);
                }
            }

            this.syncSelection();
            this.$emit('choice', 'random');
        },

        updateSelections() {
            const game = this.game;
            const { amounts, counts, combinations, items } = game;
            const { action } = this;

            // Amount
        
            if (!amounts[this.amountId]) {
                for (const [ id, amount ] of Object.entries(amounts)) {
                    if (amount.default) {
                        this.amountId = id;
                        break;
                    }
                }
            }

            // Flask
            if (game.name == 'Jokerjackpot') {
                const usedFlasks = {};

                for (const gameItemId of this.gamesItems) {
                    const item = items[gameItemId];

                    const previousItemId = usedFlasks[item.flask];
                    if (previousItemId) {
                        this.gamesItems = this.gamesItems.filter(itemId => itemId != previousItemId)
                    }

                    usedFlasks[item.flask] = gameItemId;
                }
            }

            // Combinations
            if(game.name == 'Jokerjackpot') {
                let combinatorsCount = 0;

                for (const gameItemId of this.gamesItems) {
                    const item = items[gameItemId];

                    if (item.symbol == '*') {
                        combinatorsCount++;
                    }
                }

                while (combinatorsCount > 2) {
                    for (const gameItemId of this.gamesItems) {
                        const item = items[gameItemId];

                        if (item.symbol == '*') {
                            this.gamesItems = this.gamesItems.filter(itemId => gameItemId != itemId);
                            combinatorsCount--;
                            break;
                        }
                    }
                }

                for (const [ id, combination ] of Object.entries(combinations)) {
                    if (combination.combinators == combinatorsCount) {
                        this.combinationId = id;

                        if (this.amountId == head(Object.keys(amounts))) {
                            break;
                        }
                    }
                }
            }

            // Count
            if (game.name == 'Jokerjackpot') {
                const combination = combinations[this.combinationId];

                if (combination) {
                    this.countId = combination.count_id;
                }
            } else if (action == 'item') {
                const itemCount = this.gamesItems.length;
                let newCountId = false

                for (const [ id, count ] of Object.entries(counts)) {
                    if (count.count == itemCount) {
                        newCountId = id;
                        break;
                    }
                }

                if(newCountId) {
                    this.countId = newCountId;
                } else {
                    const largestCount = last(values(counts));

                    if (largestCount.count < this.gamesItems.length) {
                        this.gamesItems.splice(0, 1);
                    } else {
                        this.countId = head(Object.keys(counts));
                    }
                }

            } else if (counts[this.countId]) {
                const { count } = counts[this.countId];

                const over = this.gamesItems.length - count;

                this.gamesItems.splice(0, over);
            } else {
                const firstCountId = head(Object.keys(counts));

                this.countId = firstCountId;
            }

            // Combinations
            if (game.name == 'Interjackpot') {
                if (action == 'item' || action == 'count' || !combinations[this.combinationId]) {
                    for (const [ id, combination ] of Object.entries(combinations)) {
                        if (this.countId == combination.count_id) {
                            this.combinationId = id;
                            break;
                        }
                    }
                } else if (action == 'combination' && combinations[this.combinationId]) {
                    const { count_id } = combinations[this.combinationId];

                    this.countId = count_id;
                }
            }

            this.syncSelection();
        },
    },

    watch: {
        purchaseGameId(gameId) {
            this.gamesItems = [];

            if (this.selectionsOfGames[gameId]) {
                const { countId, amountId } = this.selectionsOfGames[gameId];

                this.countId = countId;
                this.amountId = amountId;
            } else {
                this.countId = undefined;
                this.amountId = undefined;
            }

            this.action = undefined;

            this.updateSelections();
        },

        selectionIsAdded(isAdded) {
            if (isAdded) {
                this.gamesItems = [];
                this.continueCount = 1;
                this.updateSelections();
                this.setSelectionAdded(false);
            }
        },

        usingSymbols(ids) {
            if (ids.length) {
                this.gamesItems = [ ...ids ];
                this.syncSelection();
                this.action = 'item';
                this.updateSelections();
                this.$emit('statSymbolsSet');
            }
        },

        favoritesActive() {
            this.$nextTick(() => syncFrameHeight());
        },

        suggestionsActive() {
            this.$nextTick(() => syncFrameHeight());
        },
    },

    mounted() {
        if (this.purchaseGameId) {
            this.updateSelections();
        }
    },
}
</script>

